import {
  ANY_DEVICE_OWNER_FLAG,
  NO_DEVICE_OWNER_FLAG,
} from '#components/patterns/entity-select/device-owner/option-constants.ts';
import { NO_ROLE_FLAG } from '#components/patterns/entity-select/role/option-constants.ts';
import type { ResourceListOptions } from '#dn-types/api.ts';
import type { EffectiveAccessEntry } from '#dn-types/effective-access.ts';
import type { ConfigOverride, Host } from '#dn-types/host.ts';
import { HOST_FILTERS } from '#hooks/useHostFilters.ts';
import { hostFlavorFlags, isHostKind, type HostFlavorFlags } from '#util/host.ts';
import { rewordValidationMessages, trackError } from './errors.ts';
import { del, get, getList, post, put } from './fetch.ts';

export const DEFAULT_HOSTS_PAGE_SIZE = 25;

// The hosts API rejects search filters shorter than this with a 400, so both the search input (write)
// and the list query (read) must enforce it.
export const MIN_SEARCH_LENGTH = 2;

export const NULL_FILTER_VALUE = 'null';
export const ANY_FILTER_VALUE = 'any';
// This is a mapping between filter names and the flags we use to indicate "none of this thing"
const NO_ENTITY_FLAGS: { [k: string]: string } = {
  roleID: NO_ROLE_FLAG,
  endpointOIDCUserID: NO_DEVICE_OWNER_FLAG,
};
const ANY_ENTITY_FLAGS: { [k: string]: string } = {
  endpointOIDCUserID: ANY_DEVICE_OWNER_FLAG,
};
// `type` is excluded because it isn't a wire param: it expands to the isLighthouse/isRelay pair in
// createEncodedGetHostsFilter before the per-key translation runs.
const SCALAR_HOST_FILTERS = HOST_FILTERS.filter(
  (f): f is Exclude<(typeof HOST_FILTERS)[number], 'tags' | 'type'> => f !== 'tags' && f !== 'type'
);
const ALL_SCALAR_HOST_FILTERS = [...SCALAR_HOST_FILTERS, 'isLighthouse', 'isRelay', 'search'] as const;
type ScalarHostFilterName = (typeof ALL_SCALAR_HOST_FILTERS)[number];

function isScalarHostFilterKey(key: string): key is ScalarHostFilterName {
  return (ALL_SCALAR_HOST_FILTERS as readonly string[]).includes(key);
}

// This function takes a GetHostsFilter and outputs a set of keys and values that are valid to provide to GET /hosts
// Tags are excluded here and handled separately via appendTagFilters
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createEncodedGetHostsFilter({ tags, type, ...filter }: GetHostsFilter = {}) {
  return Object.fromEntries(
    Object.entries<boolean | string | undefined>({
      // `type` values come straight from the URL, so guard before expanding — an unrecognized value
      // means "no type filter" rather than a 400 from the API.
      ...(typeof type === 'string' && isHostKind(type) ? hostFlavorFlags(type) : {}),
      ...filter,
    })
      .map(([key, val]) => translateFilterToAPI(key, val))
      // Remove any "empty" filters
      .filter(([, val]) => val !== '')
  );
}

/** Map the filter params we use in the frontend URLs to what the API is expecting  */
function translateFilterToAPI(filterName: string, value: boolean | string | undefined) {
  if (!isScalarHostFilterKey(filterName)) {
    trackError(`tried to filter using unsupported name: ${filterName}`);
    return [filterName, ''] as const;
  }

  // Check if this a "null" filter
  if (value === NO_ENTITY_FLAGS[filterName]) {
    return [`filter.${filterName}`, NULL_FILTER_VALUE] as const;
  }

  // Check if this an "any" filter
  if (value === ANY_ENTITY_FLAGS[filterName]) {
    return [`filter.${filterName}`, ANY_FILTER_VALUE] as const;
  }

  switch (filterName) {
    case 'updateAvailable':
      return ['filter.metadata.updateAvailable', 'true'] as const;
    case 'unenrolled':
      return ['filter.metadata.lastSeenAt', NULL_FILTER_VALUE] as const;
    case 'mobile':
      return ['filter.metadata.platform', 'mobile'] as const;
    case 'isBlocked':
    case 'networkID':
    case 'roleID':
    case 'endpointOIDCUserID':
    case 'isLighthouse':
    case 'isRelay':
    case 'search':
      return [`filter.${filterName}`, String(value ?? '')] as const;
  }
}

/** Appends individual `filter.tag` params for each tag in the filter */
function appendTagFilters(params: URLSearchParams, filter?: GetHostsFilter) {
  if (filter?.tags) {
    for (const tag of filter.tags) {
      params.append('filter.tag', tag);
    }
  }
}

export type GetHostsFilter = { [P in ScalarHostFilterName]?: boolean | string } & {
  /** Loosely typed because values arrive from the URL; non-{@link HostKind} values are dropped. */
  type?: boolean | string;
  tags?: string[];
};

/** Filter presets for the host/lighthouse/relay kind slices of the hosts collection. */
export const HOST_FILTER = { type: 'host' } as const satisfies GetHostsFilter;
export const LIGHTHOUSE_FILTER = { type: 'lighthouse' } as const satisfies GetHostsFilter;
export const RELAY_FILTER = { type: 'relay' } as const satisfies GetHostsFilter;

// GET /v2/hosts
export type GetHostsResult = Host[];
type GetHostsVariables = ResourceListOptions & { filter?: GetHostsFilter };
export async function getHosts({ cursor, pageSize = DEFAULT_HOSTS_PAGE_SIZE, filter, init }: GetHostsVariables = {}) {
  const params = new URLSearchParams({
    includeCounts: 'true',
    pageSize: pageSize.toString(),
    ...createEncodedGetHostsFilter(filter),
  });

  appendTagFilters(params, filter);

  if (cursor) {
    params.append('cursor', cursor);
  }

  return getList<GetHostsResult>(`/v2/hosts?${params.toString()}`, init).then((res) => {
    const page = res.metadata.page;
    // If the user paginates backwards, we may send a cursor which results in loading the "first" page of results
    // but with fewer results than the standard page size. Refetch the first page without a cursor to get a full result set.
    if (page?.start === 0 && page.count < pageSize && cursor) {
      params.delete('cursor');
      return getList<GetHostsResult>(`/v2/hosts?${params.toString()}`, init);
    }
    return res;
  });
}

// GET /v2/hosts/:id
export type GetHostResult = Host;
export function getHost(id: string, init?: RequestInit) {
  return get<GetHostResult>(`/v2/hosts/${encodeURIComponent(id)}`, init);
}

// GET /v1/hosts/:id/effective-access
export type GetHostEffectiveAccessResult = EffectiveAccessEntry[];
export function getHostEffectiveAccess(id: string, init?: RequestInit) {
  return get<GetHostEffectiveAccessResult>(`/v1/hosts/${encodeURIComponent(id)}/effective-access`, init);
}

// POST /v2/hosts
export type AddHostVariables = {
  name: string;
  networkID: string;
  staticAddresses?: string[];
  roleID: string | null;
  listenPort: number;
  ipAddresses?: string[];
  tags: string[];
  configOverrides: ConfigOverride[];
} & HostFlavorFlags;
export type AddHostResult = Host;
export function addHost(formData: AddHostVariables) {
  return (
    post<Host>('/v2/hosts', formData)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordHostValidationMessages(err);
      })
  );
}

// PUT /v3/hosts/:id
export type EditHostVariables = {
  id: string;
  values: {
    name: string;
    roleID: string | null;
    staticAddresses?: string[];
    listenPort: number;
    tags: string[];
    configOverrides: ConfigOverride[];
  };
};
export type EditHostResult = Host;
export function editHost(input: EditHostVariables) {
  const { id, values } = input;
  return put<Host>(`/v3/hosts/${encodeURIComponent(id)}`, values).catch((err: unknown) => {
    rewordHostValidationMessages(err);
  });
}

// DELETE /hosts/:id
export type DeleteHostResult = Record<string, never>;
export function deleteHost(id: string) {
  return del<DeleteHostResult>(`/v1/hosts/${encodeURIComponent(id)}`);
}

// POST /v2/hosts/:id/block
export type BlockHostResult = Host;
export function blockHost(id: string) {
  return post<BlockHostResult>(`/v2/hosts/${encodeURIComponent(id)}/block`).catch((err: unknown) => {
    rewordHostValidationMessages(err);
  });
}

// POST /v2/hosts/:id/unblock
export type UnblockHostResult = Host;
export function unblockHost(id: string) {
  return post<UnblockHostResult>(`/v2/hosts/${encodeURIComponent(id)}/unblock`).catch((err: unknown) => {
    rewordHostValidationMessages(err);
  });
}

// POST /hosts/:id/enrollment-code
export type GetEnrollmentTokenResult = {
  code: string;
  lifetimeSeconds: number;
};
export function getEnrollmentToken(id: string, init?: RequestInit) {
  return post<GetEnrollmentTokenResult>(`/v1/hosts/${encodeURIComponent(id)}/enrollment-code`, init);
}

// POST /hosts/:id/enrollment-code-check
export type CheckEnrollmentTokenResult = {
  exists: boolean;
};
export function checkEnrollmentToken(hostId: string, code: string, init?: RequestInit) {
  return post<CheckEnrollmentTokenResult>(
    `/v1/hosts/${encodeURIComponent(hostId)}/enrollment-code-check`,
    { code },
    init
  );
}

// POST /v2/hosts/:hostId/ip-addresses
export type AddHostIPVariables = {
  hostId: string;
  ipAddress: string;
};
export type AddHostIPResult = Host;
export function addHostIP({ hostId, ipAddress }: AddHostIPVariables) {
  return post<AddHostIPResult>(`/v2/hosts/${encodeURIComponent(hostId)}/ip-addresses`, { ipAddress }).catch(
    (err: unknown) => {
      rewordHostValidationMessages(err);
    }
  );
}

// POST /v2/host-and-enrollment-code
export type AddHostAndEnrollmentCodeVariables = AddHostVariables;
export type AddHostAndEnrollmentCodeResult = {
  host: Host;
  enrollmentCode: {
    code: string;
    lifetimeSeconds: number;
  };
};
export function addHostAndEnrollmentCode(formData: AddHostAndEnrollmentCodeVariables) {
  return post<AddHostAndEnrollmentCodeResult>('/v2/host-and-enrollment-code', formData).catch((err: unknown) => {
    rewordHostValidationMessages(err);
  });
}

export function rewordHostValidationMessages(err: unknown): never {
  rewordValidationMessages(err, (details) => {
    const { path, code, extra } = details;
    if (path === undefined && code === 'ERR_LIMIT_EXCEEDED') {
      if (typeof extra?.limit === 'number') {
        return `You have reached the limit of ${extra.limit} hosts (including lighthouses and relays).  Upgrade to the Pro plan to create more hosts.`;
      }
      return 'You have the maximum number of hosts allowed. Upgrade to the Pro plan to create more.';
    }
    if (path === undefined && code === 'ERR_NETWORK_FULLY_ALLOCATED') {
      return 'You have reached the maximum number of IP addresses in this network';
    }
    if (path === 'tags' && code === 'ERR_LIMIT_EXCEEDED') {
      if (typeof extra?.limit === 'number') {
        return `You have reached the limit of ${extra.limit} tags. Delete some unused tags before creating more.`;
      }
      return 'You have the maximum number of tags allowed. Delete some unused tags before creating more.';
    }
    if (path === 'name' && code === 'ERR_DUPLICATE_VALUE') {
      return 'You already have a host or lighthouse with this name, try another.';
    }
    // The add-IP endpoint (POST /v2/hosts/:id/ip-addresses) reports validation errors under the
    // singular `ipAddress` path, while host create/edit report them under indexed `ipAddresses[...]`.
    const isIpAddressPath = path === 'ipAddress' || (path?.startsWith('ipAddresses[') ?? false);
    if (isIpAddressPath && code === 'ERR_DUPLICATE_VALUE') {
      return 'This IP address is already in use, try another.';
    }
    if (isIpAddressPath && code === 'ERR_OUT_OF_RANGE') {
      return 'Not within network range.';
    }
    if (isIpAddressPath && code === 'ERR_INVALID_IPV6_ADDRESS') {
      return 'Must be a valid IPv6 address and must not be a loopback address.';
    }
    if (isIpAddressPath && code === 'ERR_HOST_ALREADY_HAS_IPV4') {
      return 'This host already has an IPv4 address.';
    }
    if (path === 'staticAddresses' && code === 'ERR_INVALID_HOSTNAME_OR_IPV4_ADDRESS_PORT') {
      return 'Public IP/Hostname must be a valid IPv4 address or a valid hostname and must not be a loopback address';
    }
  });
}

export type SendCommandResult = Record<string, unknown>;
export type Command =
  | 'PrintTunnel'
  | 'PrintCert'
  | 'CreateTunnel'
  | 'QueryLighthouse'
  | 'ListCommands'
  | 'DebugStack'
  | 'StreamLogs'
  | 'Restart';
export type SendCommandVariables = {
  command: Command;
  args: Record<string, unknown>;
};
// POST /hosts/:id/command
export function sendCommand(hostId: string, command: Command, args?: Record<string, unknown>, init?: RequestInit) {
  return post<SendCommandResult>(`/v1/hosts/${encodeURIComponent(hostId)}/command`, { command, args }, init);
}
